import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  AdminOrganisationDetail,
  AdminOrganisationVault,
} from "pages/geoscape-admin/organisation/organisation-details/utils";
import {
  ClipConfiguration,
  ClipInvoice,
  PaginatedClipsResponse,
  QuoteSummary,
} from "pages/geoscape-data/clip/clip-ui/models";
import { makeApiRequestToUrl } from "utils/api-client";
import {
  AdminAccountDetails,
  PaginatedEmailLogResponse,
  AdminOrganisationInfo,
  AdminSearchAccount,
  AdminSearchOrganisation,
  AdminSubscription,
  ApiKey,
  Connector,
  CreditsInfo,
  CustomDatasetDefinitions,
  CustomDatasetFilterType,
  CustomDatasetList,
  Dataset,
  DatasetDefinitions,
  DatasetFiltersType,
  DatasetList,
  DemoConfiguration,
  HubPlan,
  PaymentMethodAdmin,
  Pipeline,
  PipelineLastRun,
  PipelineNextRun,
  PlanPermissions,
  Release,
  S3ContentsResponse,
  SSHKeyPairResponse,
  UserLicences,
  UserMembership,
} from "./models";

async function fetchPaymentMethodAdmin(
  organisationId: string,
): Promise<PaymentMethodAdmin | null> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/${organisationId}/paymentMethod`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    const paymentMethods: PaymentMethodAdmin[] = responseData.filter(
      (paymentMethod: PaymentMethodAdmin) => paymentMethod.active,
    );

    if (paymentMethods.length !== 0) {
      return paymentMethods[0];
    } else {
      return null;
    }
  } catch (err) {
    throw new Error(`Unable to fetch payment method: ${err}`);
  }
}

async function fetchCustomDatasetsAdmin(
  queryFields: CustomDatasetFilterType,
  apigeeDeveloperId: string,
  limit: number = 100,
  offset: number = 0,
): Promise<CustomDatasetList> {
  try {
    const queryFields_update: any = { ...queryFields };

    queryFields_update.release = queryFields_update.latestRelease
      ? "latest"
      : queryFields_update.release;
    delete queryFields_update.latestRelease;

    queryFields_update.product = queryFields_update.name
      ? queryFields_update.name.map((name: string) => name.toUpperCase())
      : "";
    delete queryFields_update.name;

    let searchParams = new URLSearchParams();
    searchParams.set("limit", `${limit}`);
    searchParams.set("offset", `${offset}`);

    Object.entries(queryFields_update).forEach(([key, value]: any) =>
      value.toString().length ? searchParams.set(key, value) : "",
    );

    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/custom/developer/${apigeeDeveloperId}?${searchParams.toString()}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch all custom datasets as admin for account '${apigeeDeveloperId}' : ${err}`,
    );
  }
}

async function fetchAdminDatasets(
  queryFields: DatasetFiltersType,
  limit: number = 100,
  offset: number = 0,
): Promise<DatasetList> {
  try {
    const queryFields_update: any = { ...queryFields };

    queryFields_update.release = queryFields_update.latestRelease
      ? "latest"
      : queryFields_update.release;
    delete queryFields_update.latestRelease;

    let searchParams = new URLSearchParams();
    searchParams.set("limit", `${limit}`);
    searchParams.set("offset", `${offset}`);
    Object.entries(queryFields_update).forEach(([key, value]: any) =>
      value.toString().length ? searchParams.set(key, value) : "",
    );

    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/search?${searchParams.toString()}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch all datasets as admin: ${err}`);
  }
}

async function fetchAdminDatasetsLink(datasetId: string): Promise<any> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/download/${datasetId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData.datasets[0].downloadLink;
  } catch (err) {
    throw new Error(
      `Unable to download dataset as admin for dataset '${datasetId}' : ${err}`,
    );
  }
}

async function fetchAdminCustomDatasetsLink(datasetId: string): Promise<any> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/custom/${datasetId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }
    return responseData.datasets[0].downloadLink;
  } catch (err) {
    throw new Error(
      `Unable to download custom dataset '${datasetId}' : ${err}`,
    );
  }
}

async function fetchAdminCustomDatasetsSearch(
  queryFields: CustomDatasetFilterType,
  limit: number = 100,
  offset: number = 0,
): Promise<CustomDatasetList> {
  try {
    const queryFields_update: any = { ...queryFields };

    queryFields_update.release = queryFields_update.latestRelease
      ? "latest"
      : queryFields_update.release;
    delete queryFields_update.latestRelease;

    queryFields_update.product = queryFields_update.name
      ? queryFields_update.name.map((name: string) => name.toUpperCase())
      : "";
    delete queryFields_update.name;

    let searchParams = new URLSearchParams();

    searchParams.set("limit", `${limit}`);
    searchParams.set("offset", `${offset}`);

    Object.entries(queryFields_update).forEach(([key, value]: any) =>
      value.toString().length ? searchParams.set(key, value) : "",
    );

    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/custom/search?${searchParams.toString()}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch all custom datasets as admin : ${err}`);
  }
}

async function fetchDataReleases(): Promise<Release[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/release`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch data releases: ${err}`);
  }
}

async function fetchReleaseDatasets(releaseId: string): Promise<Dataset[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/release/${releaseId}/datasets`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch data release '${releaseId}' : ${err}`);
  }
}

async function fetchAdminDefinitions(): Promise<DatasetDefinitions> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/datasets/definitions`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch dataset definitions: ${err}`);
  }
}

async function fetchAdminCustomDefinitions(): Promise<CustomDatasetDefinitions> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/custom/definitions`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch admin custom dataset definitions: ${err}`);
  }
}

async function fetchPermissionsAdmin(
  organisationId: string,
  permissionSets?: string[],
): Promise<PlanPermissions> {
  try {
    const query = permissionSets?.length
      ? `?permissionSets=${encodeURIComponent(permissionSets.join(","))}`
      : "";

    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/permissions/${organisationId}${query}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch permissions as admin for organisation id ${organisationId}: ${err}`,
    );
  }
}

// Query for fetching the Users in the admin portal search
async function fetchOrganisationsAdminSearch(
  query: string,
): Promise<AdminSearchOrganisation[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/search?query=${encodeURIComponent(query)}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch users with query '${query}' : ${err}`);
  }
}

async function fetchAdminUsage(
  organisationId: string,
  startTime: number,
  endTime: number,
) {
  try {
    dayjs.extend(utc);
    dayjs.extend(timezone);

    // Get the timezone string from the user's device
    const userTimezone = dayjs.tz.guess();
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/usage?organisationId=${organisationId}&offset=${userTimezone}&startTime=${startTime}&endTime=${endTime}`,
      "GET",
    );

    const responseData = await response.json(); // add type: Usage

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch usage data: ${err}`);
  }
}

async function fetchAdminCredits(organisationId: string): Promise<CreditsInfo> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/credits/${organisationId}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch credits data: ${err}`);
  }
}

async function fetchAdminUsersSearch(
  query: string,
  searchTerm: string,
): Promise<AdminSearchAccount[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/accounts/search?query=${encodeURIComponent(
        query,
      )}&term=${searchTerm}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch users with query '${query}' and term '${searchTerm}': ${err}`,
    );
  }
}

async function fetchAdminUserDetails(
  accountId: string,
): Promise<AdminAccountDetails> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/accounts/${accountId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch user details as admin for account '${accountId}' : ${err}`,
    );
  }
}

async function fetchOrganisationDetailsAdmin(
  organisationId: string,
): Promise<AdminOrganisationDetail> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/${organisationId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch user details as admin for organisation '${organisationId}' : ${err}`,
    );
  }
}

async function fetchOrganisationInfoAdmin(
  organisationId: string,
): Promise<AdminOrganisationInfo> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/${organisationId}/info`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch user details as admin for organisation '${organisationId}' : ${err}`,
    );
  }
}

async function fetchOrganisationSubscriptionAdmin(
  organisationId: string,
): Promise<AdminSubscription> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/subscriptions/${organisationId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch subscription as admin for organisation '${organisationId}' : ${err}`,
    );
  }
}

async function checkAdminNetworkAccessibility(): Promise<boolean> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/admin/network",
      "GET",
    );

    return response.status === 200;
  } catch (err) {
    return false;
  }
}

async function fetchAdminOrgMembers(orgId: string): Promise<any> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/members?organisationId=${encodeURIComponent(orgId)}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch organisation members as admin for organisation '${orgId}' : ${err}`,
    );
  }
}

// last_key is the pagination cursor from a prior page; limit overrides the server default (used by
// the compact user-page preview to pull just the newest few).
function emailLogPageQuery(pageParam?: string, limit?: number): string {
  const params = new URLSearchParams();
  if (limit !== undefined) params.set("limit", String(limit));
  if (pageParam) params.set("last_key", pageParam);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

async function fetchAdminOrganisationEmailsPaginated(
  organisationId: string,
  pageParam?: string,
  limit?: number,
): Promise<PaginatedEmailLogResponse> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/${organisationId}/emails/paginated${emailLogPageQuery(pageParam, limit)}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(
        responseData.messages?.join(". ") || "Failed to fetch email log",
      );
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch email log as admin for organisation '${organisationId}' : ${err}`,
    );
  }
}

async function fetchAdminUserEmailsPaginated(
  userId: string,
  pageParam?: string,
  limit?: number,
): Promise<PaginatedEmailLogResponse> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/emails/user/${userId}/paginated${emailLogPageQuery(pageParam, limit)}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(
        responseData.messages?.join(". ") || "Failed to fetch email log",
      );
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch email log as admin for user '${userId}' : ${err}`,
    );
  }
}

async function fetchAllAdminEmailsPaginated(
  pageParam?: string,
): Promise<PaginatedEmailLogResponse> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/emails/paginated${
        pageParam ? `?last_key=${encodeURIComponent(pageParam)}` : ""
      }`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(
        responseData.messages?.join(". ") || "Failed to fetch email log",
      );
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch paginated email log as admin : ${err}`);
  }
}

// Fetched once and reused (react-query cached) to enrich the email log with org names, so the
// paginated email endpoint doesn't re-scan the org table on every page.
async function fetchAllOrganisationNames(): Promise<Record<string, string>> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/names`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(
        responseData.messages?.join(". ") || "Failed to fetch organisation names",
      );
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch organisation names as admin : ${err}`);
  }
}

async function fetchAdminUserMembership(
  orgId: string,
  accountId: string,
): Promise<UserMembership> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/${orgId}/${accountId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch organisation members as admin for organisation '${orgId}' : ${err}`,
    );
  }
}

async function fetchAdminUserMemberships(
  userId: string,
): Promise<UserMembership[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/organisation/user/membership?userId=${encodeURIComponent(
        userId,
      )}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch user memberships as admin for the user '${userId}' : ${err}`,
    );
  }
}

async function fetchAvailableSubscriptions(): Promise<HubPlan[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/subscriptions/all`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch all available subscriptions: ${err}`);
  }
}

async function fetchAdminUserApiKeys(
  organisationId: string,
): Promise<ApiKey[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/apiKeys?organisationId=${organisationId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch api keys as admin for user '${organisationId}' : ${err}`,
    );
  }
}

async function fetchAdminUserApiKey(
  apiKeyName: string,
  organisationId: string,
): Promise<ApiKey> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/apiKeys/${apiKeyName}?organisationId=${organisationId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch api key for user as admin for user '${organisationId}' and api key '${apiKeyName}': ${err}`,
    );
  }
}

async function fetchAdminDatasetLicences(
  organisationId: string,
): Promise<UserLicences> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/licences/${organisationId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch admin dataset licences for organisation '${organisationId}': ${err}`,
    );
  }
}

async function fetchAdminClips(): Promise<ClipConfiguration[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/clip/config`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch all clips for admin: ${err}`);
  }
}

async function fetchAdminClipsPaginated(
  pageParam?: string,
): Promise<PaginatedClipsResponse> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/clip/config/paginated${
        pageParam ? `?last_key=${encodeURIComponent(pageParam)}` : ""
      }`,
      "GET",
    );
    const responseData = await response.json();
    if (response.status !== 200) {
      throw new Error(
        responseData.messages?.join(". ") || "Failed to fetch clips",
      );
    }
    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch paginated clips: ${err}`);
  }
}

async function fetchAdminClipSummary(clipId: string): Promise<QuoteSummary> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/clip/summary/${clipId}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch clip summary for admin '${clipId}' : ${err}`,
    );
  }
}

async function fetchAdminClipInvoice(clipId: string): Promise<ClipInvoice> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/clip/${clipId}/invoice`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch clip invoice for admin '${clipId}' : ${err}`,
    );
  }
}

async function fetchAdminUserVault(
  accountId: string,
): Promise<AdminOrganisationVault> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/vault/${accountId}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch admin user note for the account: ${accountId} : ${err}`,
    );
  }
}

async function fetchAdminDemos(): Promise<DemoConfiguration[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      "/admin/demos",
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(`Unable to fetch admin demos. ${err}`);
  }
}

async function fetchClipsbyOrgId(orgId: string): Promise<ClipConfiguration[]> {
  const url = `${import.meta.env.VITE_DELIVERY_API_URL}`;

  try {
    const response = await makeApiRequestToUrl(
      url,
      `/admin/clip/list/${orgId}`,
      "GET",
    );

    const responseData = (await response.json()) as ClipConfiguration[];

    if (!response.ok) {
      throw new Error(
        (responseData as { messages?: string[] }).messages?.join(". ") ||
          `Request failed with status ${response.status}`,
      );
    }

    return responseData;
  } catch (error) {
    throw new Error(
      `Unable to fetch organisation clips from '${orgId}': ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
    );
  }
}

async function fetchAdminConnectors(
  organisationId: string,
): Promise<Connector[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/connectors/${organisationId}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch admin connectors for org ${organisationId}. ${err}`,
    );
  }
}

async function fetchAdminConnectorById(
  organisationId: string,
  connectorId: string,
): Promise<Connector> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/connectors/${organisationId}/${connectorId}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch admin connector by id ${connectorId}. ${err}`,
    );
  }
}

async function fetchAdminPipelines(
  organisationId: string,
): Promise<Pipeline[]> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/pipelines/${organisationId}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch admin pipelines for org ${organisationId}. ${err}`,
    );
  }
}

async function fetchAdminPipelineById(
  organisationId: string,
  pipelineId: string,
): Promise<Pipeline> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/pipelines/${pipelineId}/organisation/${organisationId}`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch admin pipeline by id ${pipelineId}. ${err}`,
    );
  }
}

async function fetchAdminPipelineLastRun(
  pipelineId: string,
): Promise<PipelineLastRun> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/pipelines/${pipelineId}/lastrun`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch admin pipeline lastrun status for pipeline ${pipelineId}. ${err}`,
    );
  }
}

async function fetchAdminPipelineNextRun(
  pipelineId: string,
): Promise<PipelineNextRun> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/pipelines/${pipelineId}/nextrun`,
      "GET",
    );

    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch admin pipeline next run status for pipeline ${pipelineId}. ${err}`,
    );
  }
}

const getOrCreateSSHKeyPair = async (
  organisationId: string,
): Promise<SSHKeyPairResponse> => {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/connectors/${organisationId}/ssh-keypair`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(responseData.messages.join(". "));
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to get or create SSH key pair for organisation '${organisationId}' : ${err}`,
    );
  }
};

async function fetchS3BucketContents(
  basePath: string,
  bucketKey: string,
): Promise<S3ContentsResponse> {
  try {
    const response = await makeApiRequestToUrl(
      `${import.meta.env.VITE_DELIVERY_API_URL}`,
      `/admin/datasets/custom/s3/list?base_path=${encodeURIComponent(
        basePath,
      )}&bucket=${encodeURIComponent(bucketKey)}`,
      "GET",
    );
    const responseData = await response.json();

    if (response.status !== 200) {
      throw new Error(
        responseData.messages?.join(". ") || "Failed to fetch S3 contents",
      );
    }

    return responseData;
  } catch (err) {
    throw new Error(
      `Unable to fetch S3 bucket contents for path '${basePath}': ${err}`,
    );
  }
}

export {
  checkAdminNetworkAccessibility,
  fetchAdminClipInvoice,
  fetchAdminClips,
  fetchAdminClipsPaginated,
  fetchAdminClipSummary,
  fetchAdminConnectorById,
  fetchAdminConnectors,
  fetchAdminCredits,
  fetchAdminCustomDatasetsLink,
  fetchAdminCustomDatasetsSearch,
  fetchAdminCustomDefinitions,
  fetchAdminDatasetLicences,
  fetchAdminDatasets,
  fetchAdminDatasetsLink,
  fetchAdminDefinitions,
  fetchAdminDemos,
  fetchAdminOrgMembers,
  fetchAdminOrganisationEmailsPaginated,
  fetchAdminUserEmailsPaginated,
  fetchAllAdminEmailsPaginated,
  fetchAllOrganisationNames,
  fetchAdminPipelineById,
  fetchAdminPipelineLastRun,
  fetchAdminPipelineNextRun,
  fetchAdminPipelines,
  fetchAdminUsage,
  fetchAdminUserApiKey,
  fetchAdminUserApiKeys,
  fetchAdminUserDetails,
  fetchAdminUserMembership,
  fetchAdminUserMemberships,
  fetchAdminUserVault as fetchAdminUserNote,
  fetchAdminUsersSearch,
  fetchAvailableSubscriptions,
  fetchClipsbyOrgId,
  fetchCustomDatasetsAdmin,
  fetchDataReleases,
  fetchOrganisationDetailsAdmin,
  fetchOrganisationInfoAdmin,
  fetchOrganisationsAdminSearch,
  fetchOrganisationSubscriptionAdmin,
  fetchPaymentMethodAdmin,
  fetchPermissionsAdmin,
  fetchReleaseDatasets,
  fetchS3BucketContents,
  getOrCreateSSHKeyPair,
};
